import store from '@/store';

// NOTE: Do not use: includes full lodash lib
// ==========================================
// import resolveConfig from 'tailwindcss/resolveConfig';
import tailwind from '@/../tailwind.config.js';

import SetupApi from '@/store/api/setup';
import { default as router, resetRouter } from '@/router';
import { get } from '@/helpers';

import PasswordResetForm from '@/extensions/restricted-area/views/PasswordResetForm';
import EmailVerificationForm from '@/extensions/restricted-area/views/EmailVerificationForm';

// import AuthApi from '@/extensions/restricted-area/store/api/auth';

// initial state
const state = {
  initialised: false,
  loaded: false,
  error: false,
  CMS: {},
  ui: {
    sidebar: false // sidebar visibility
  },
  webpSupport: undefined
};

const getters = {
  initialised: state => state.initialised,
  loaded: state => state.loaded,
  error: state => state.error,
  CMS: state => state.CMS,
  config: state => state.CMS.config,
  menus: state => state.CMS.menus,
  messages: state => state.messages,
  sidebar: state => state.ui.sidebar,

  tailwind: () => tailwind,
  webpSupport: state => state.webpSupport,

  logo: state => {
    let logo = {};
    if (state.CMS.images && state.CMS.images.length > 0) {
      logo = state.CMS.images.find(image => image.type === 'cms-logo');
    }
    return logo;
  },

  googleRecaptchaSiteKey: state => {
    const apiKeys = get(state.CMS, 'apiKeys') || [];
    const apiKey = apiKeys.find(apiKey => apiKey.key === 'google-recaptcha-v3');

    let googleRecaptchaSiteKey;
    if (apiKey) {
      googleRecaptchaSiteKey = apiKey.public;
    }
    return googleRecaptchaSiteKey;
  },

  mapboxKey: state => {
    const apiKeys = get(state.CMS, 'apiKeys') || [];
    const apiKey = apiKeys.find(apiKey => apiKey.key === 'mapbox');

    let mapboxKey;
    if (apiKey) mapboxKey = apiKey.public;
    return mapboxKey;
  }
};

const actions = {
  async initialise({ commit, dispatch, getters }, { base_url }) {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');
    if (token) {
      commit('auth/addTokenToState', token, { root: true });
    }

    // detect webp support
    const elem = document.createElement('canvas');
    if (elem.getContext && elem.getContext('2d')) {
      // was able or not to get WebP representation
      const supported = elem.toDataURL('image/webp').indexOf('data:image/webp') == 0;
      commit('setWebpSupport', supported);
    } else {
      commit('setWebpSupport', false);
    }

    let actions = [];
    const fetchCms = SetupApi.fetchCms(base_url)
      .then(cms => {
        if (cms) {
          commit('initialise', cms);
          // commit('mapRouter');
        } else {
          commit('setError', { code: 404, message: 'Not Found!' });
        }
      })
      .catch(error => commit('fetchFailed', error));

    actions.push(fetchCms);
    if (getters['auth/isAuthenticated']) {
      const fetchAuthenticatedMember = dispatch('auth/fetchAuthenticatedMember');
      actions.push(fetchAuthenticatedMember);
    } else {
      commit('shop/setCartInitialised', false, { root: true });
      dispatch('shop/initCart', null, { root: true });
    }

    return Promise.all(actions).then(() => {
      commit('mapRouter');
    });
  }
};

const mutations = {
  initialise(state, cms) {
    state.CMS = cms;
    state.initialised = true;
  },
  loaded(state, value) {
    if (state.initialised && value) state.loaded = value;
    else state.loaded = false;
  },
  setWebpSupport(state, supported) {
    state.webpSupport = supported;
  },
  updateSidebarVisibility(state, value) {
    state.ui.sidebar = value;
  },
  mapRouter(state) {
    let loadingRoutes = [];

    const staticRoutes = [
      {
        path: '/resetpassword/:email/:token',
        name: 'Passwort zurücksetzen',
        component: PasswordResetForm,
        meta: {
          icon: null,
          title: state.CMS.name ? `${state.CMS.name} - Passwort zurücksetzen` : 'Passwort zurücksetzen',
          skipMiddleware: true
        }
      },
      {
        path: '/verify/:id/:hash/:expires/:signature',
        name: 'Account bestätigen',
        component: EmailVerificationForm,
        meta: {
          icon: null,
          title: state.CMS.name ? `${state.CMS.name} - Account bestätigen` : 'Account bestätigen',
          skipMiddleware: true
        }
      },
      {
        path: '/details/:product',
        name: 'product-details',
        component: () => import('@/extensions/shop/views/Product.vue'),
        meta: {
          icon: null,
          title: state.CMS.name ? `${state.CMS.name} - Produktdetails` : 'Produktdetails',
          skipMiddleware: true
        }
      },
      {
        path: '/blog/:key/:src?',
        name: 'blog',
        // component: () => import('@/views/CategoryPost.vue'),
        meta: {
          icon: null,
          title: state.CMS.name ? `${state.CMS.name} - Blog` : 'Blog',
          category: {
            key: 'blog'
          }
        }
      },
      {
        path: '*',
        name: 'Page Not Found',
        component: () => import('@/views/PageNotFound.vue'),
        meta: {
          icon: null,
          title: state.CMS.name ? `${state.CMS.name} - Page Not Found` : 'Page Not Found',
          skipMiddleware: true
        }
      }
      // add static category routes
    ];
    const isAuthenticated = store.getters['auth/isAuthenticated'];

    if (state.CMS.menus && state.CMS.menus.length) {
      const nodeRoutes = data => {
        let routes = [];
        let items = data.items;

        items.forEach((item, index) => {
          if (item.disabled) return true;

          // if (item.restriction === 'members' && !isAuthenticated) return true;
          // else if (item.restriction === 'guests' && isAuthenticated) return true;

          if (item.restriction === 'guests' && isAuthenticated) return true;

          if (item.disabled) return true;

          // let children = null;
          // if (item.children) {
          //   children = nodeRoutes({ items: item.children });
          // }

          if (item.page && item.page.view === 'Folder') return true;

          let component, view;
          if (item.restriction === 'members' && !isAuthenticated) {
            component = () => import(`@/extensions/restricted-area/views/Unauthorized.vue`);
            view = item.page.view;
          } else if (item.page && item.page.view) {
            component = () => import(`@/views/${item.page.view}.vue`);
            if (item.page.extension && item.page.extension.key) {
              component = () => import(`@/extensions/${item.page.extension.key}/views/${item.page.view}.vue`);
            }
            view = item.page.view;
          }

          const meta = {
            rootIndex: index,
            icon: item.icon,
            title: state.CMS.name ? `${state.CMS.name} - ${item.title}` : item.title,
            pageID: item.page?.id,
            menuItemID: item.id
          };

          switch (view) {
            case 'Logout':
              return true;
            case 'UserForm': {
              const node = {
                name: 'user-form-redirect',
                path: item.path,
                redirect: `${item.path}/account`, // TODO: bestellungen
                component,
                children: [
                  {
                    name: 'user-form-orders',
                    path: 'bestellungen',
                    component: () => import(`@/extensions/restricted-area/components/UserForm/Orders.vue`),
                    meta: { ...meta, skipMiddleware: true }
                  },
                  {
                    name: 'user-form-returns',
                    path: 'ruecksendungen',
                    component: () => import(`@/extensions/restricted-area/components/UserForm/Returns.vue`),
                    meta: { ...meta, skipMiddleware: true }
                  },
                  {
                    name: 'user-form-account',
                    path: 'account',
                    component: () => import(`@/extensions/restricted-area/components/UserForm/Account.vue`),
                    meta: { ...meta, skipMiddleware: true }
                  },
                  {
                    name: 'user-form-addresses',
                    path: 'adressen',
                    component: () => import(`@/extensions/restricted-area/components/UserForm/Addresses.vue`),
                    meta: { ...meta, skipMiddleware: true }
                  }
                ]
              };
              routes.push(node);
              break;
            }
            case 'Redirect': {
              const type = get(item.page, 'hyperlink.type');
              const redirect = get(item.page, 'hyperlink.menuItem.path');
              if (type === 'internal' && redirect) {
                const node = {
                  name: item.id, // prevent duplicate named routes warning
                  path: item.path,
                  redirect,
                  meta
                };
                routes.push(node);
              }
              break;
            }
            default: {
              const node = {
                name: item.id, // prevent duplicate named routes warning
                path: item.path,
                component,
                meta
              };
              routes.push(node);
            }
          }
        });

        return routes;
      };

      let dynamicRoutes = [];
      state.CMS.menus.forEach(menu => {
        nodeRoutes({ items: menu.menuItems }).forEach(item => dynamicRoutes.push(item));
      });

      dynamicRoutes.forEach(route => {
        loadingRoutes.push(route);
      });
    }

    staticRoutes.forEach(route => {
      loadingRoutes.push(route);
    });

    resetRouter();
    loadingRoutes.forEach(route => router.addRoute(route));
    // router.addRoutes(loadingRoutes);
  },
  fetchFailed(state, error) {
    console.error(error);
    state.error = error;
  },
  setError(state, error) {
    state.error = error;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
